import { useCallback, useEffect, useRef } from "react";

export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastExecutedRef = useRef<number>(0);
  const callbackRef = useRef<T>(callback);

  // 콜백 함수가 변경될 때마다 업데이트
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // cleanup 함수
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();

      // 첫 실행이거나 대기 시간이 지났으면 즉시 실행
      if (
        lastExecutedRef.current === 0 ||
        now - lastExecutedRef.current >= delay
      ) {
        callbackRef.current.apply(null, args);
        lastExecutedRef.current = now;
        return;
      }

      // 이전 타이머가 있다면 클리어
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // 남은 시간만큼 대기 후 실행
      const remaining = delay - (now - lastExecutedRef.current);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current.apply(null, args);
        lastExecutedRef.current = Date.now();
        timeoutRef.current = null;
      }, remaining);
    },
    [delay]
  ) as T;
}
